import React, { useEffect, useState } from 'react';
import T from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm, useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
import * as actions from '../../redux/actions';
import { useFetchNotifications } from 'features/case/redux/fetchNotifications';
import { Spinner } from 'features/common';
import { Form } from 'react-bootstrap';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import { Modal } from 'features/common/MaterialBasedComponents';
import saveAs from 'save-as';
import SelectFormAdapter from 'features/common/SelectFormAdapter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { selectCurrentFolder, selectDocuments } from 'common/selectors';
import {
  getNotificationTimestamp,
  getLatestNotification,
  doesNotificationMatchJobId,
  getNotificationDownloadLink,
  getFileNameFromLink,
} from './notificationDownloadUtils';

const hasChildFolders = (folder) => {
  if (!folder || typeof folder !== 'object') {
    return false;
  }

  if (Array.isArray(folder.folders) && folder.folders.length > 0) {
    return true;
  }

  if (Array.isArray(folder.children) && folder.children.length > 0) {
    return true;
  }

  if (typeof folder.hasChildren === 'boolean') {
    return folder.hasChildren;
  }

  if (typeof folder.childCount === 'number') {
    return folder.childCount > 0;
  }

  if (typeof folder.childrenCount === 'number') {
    return folder.childrenCount > 0;
  }

  return false;
};

const QUEUED_RESPONSE_MAX_BYTES = 256 * 1024;
const normalizeSource = (value) => (value === null || typeof value === 'undefined' ? '' : String(value).trim().toLowerCase());
const isMergeSource = (item) => normalizeSource(item?.source) === 'merge';
const hasPublicHyperlinks = (item) => !!item?.hasPublicHyperlinks;
const hasCrossRefs = (item) => !!item?.hasCrossRefs || Number(item?.crossRefCount || 0) > 0;
const DOWNLOAD_NAMING_STRATEGY_DEFAULT = 'default';
const DOWNLOAD_NAMING_STRATEGY_ORIGINAL = 'original';
const DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME = 'folderSpecificName';
const DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY = 'bundleTabOnly';

const resolveDownloadNaming = ({ filenameSelector, useTabNumbers }) => {
  const normalizedSelector = normalizeSource(filenameSelector);
  let namingStrategy = DOWNLOAD_NAMING_STRATEGY_DEFAULT;
  let prependBundleTab = false;

  if (normalizedSelector === 'original') {
    namingStrategy = DOWNLOAD_NAMING_STRATEGY_ORIGINAL;
  } else if (
    normalizedSelector === normalizeSource(DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME) ||
    normalizedSelector === 'folderspecific'
  ) {
    namingStrategy = DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME;
  } else if (normalizedSelector === 'bundletab' || normalizedSelector === 'bundletabonly') {
    namingStrategy = DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY;
  } else if (normalizedSelector === 'bundletabfilename') {
    // Legacy bundleTabFilename semantics.
    namingStrategy = DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME;
    prependBundleTab = true;
  }

  if (useTabNumbers) {
    prependBundleTab = true;
  }

  return { namingStrategy, prependBundleTab };
};

export const DownloadModal = ({ isBundleTabPage, show, handleClose, selectedRows, folder }) => {
  const fileVersionOptions = [
    { name: 'case.downloadFolderModal.fileVersionOptions.originalFile', version: 'original' },
    {
      name: 'case.downloadFolderModal.fileVersionOptions.originalAsPDF',
      version: 'pdf',
    },
    {
      name: 'case.downloadFolderModal.fileVersionOptions.mostRecentReplacement',
      version: 'recent-replacement',
    },
    {
      name: 'case.downloadFolderModal.fileVersionOptions.mostRecentOCR',
      version: 'recent-ocr',
    },
  ];
  const defaultFileVersionOption =
    fileVersionOptions.find((option) => option.version === 'recent-ocr') || fileVersionOptions[0];

  const fileNameOptions = [
    { name: 'case.downloadFolderModal.fileNameOptions.fileName', fileName: 'folderSpecificName' },
    {
      name: 'case.downloadFolderModal.fileNameOptions.originalFileName',
      fileName: 'original',
    },
    { name: 'case.downloadFolderModal.fileNameOptions.bundleTabLabel', fileName: 'bundleTabOnly' },
  ];

  const { handleSubmit, reset, setValue, control } = useForm({
    defaultValues: {
      source: defaultFileVersionOption.version,
      filenameSelector: fileNameOptions[0].fileName,
      burnId: false,
      burnExternalId: false,
      burnCrossRef: false,
      hyperlink: false,
      publichyperlink: false,
      includeMerge: false,
      useTabNumbers: false,
      useBundleTabPage: false,
    },
  });
  const dispatch = useDispatch();
  const currentFolder = useSelector(selectCurrentFolder);
  const currentFolderDocuments = useSelector(selectDocuments);
  const { notifications, transientNotifications, fetchNotifications, addPendingDownload } = useFetchNotifications();

  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [queuedDownload, setQueuedDownload] = useState(null);
  const burnCrossRefValue = useWatch({ control, name: 'burnCrossRef' });
  const showHyperlinkToggle = !!burnCrossRefValue;
  const hasSelectedRows = Array.isArray(selectedRows) && selectedRows.length > 0;
  const isFolderTreeScope = !hasSelectedRows && hasChildFolders(folder);
  const isCurrentFolderMatch =
    !!folder?.id &&
    !!currentFolder?.id &&
    String(folder.id).trim() === String(currentFolder.id).trim();
  const hasMergeInSelectedRows = hasSelectedRows && selectedRows.some((row) => isMergeSource(row));
  const hasMergeInCurrentFolder =
    !hasSelectedRows &&
    isCurrentFolderMatch &&
    Array.isArray(currentFolderDocuments) &&
    currentFolderDocuments.some((document) => isMergeSource(document));
  const canDetermineMergeAvailability = hasSelectedRows || isFolderTreeScope || isCurrentFolderMatch;
  const canIncludeMerge =
    isFolderTreeScope || hasMergeInSelectedRows || hasMergeInCurrentFolder || !canDetermineMergeAvailability;
  const hasPublicHyperlinksInSelectedRows =
    hasSelectedRows && selectedRows.some((row) => hasPublicHyperlinks(row));
  const hasPublicHyperlinksInCurrentFolder =
    !hasSelectedRows &&
    isCurrentFolderMatch &&
    Array.isArray(currentFolderDocuments) &&
    currentFolderDocuments.some((document) => hasPublicHyperlinks(document));
  const canIncludePublicHyperlinks =
    isFolderTreeScope || hasPublicHyperlinksInSelectedRows || hasPublicHyperlinksInCurrentFolder;
  const hasCrossRefsInSelectedRows =
    hasSelectedRows && selectedRows.some((row) => hasCrossRefs(row));
  const hasCrossRefsInCurrentFolder =
    !hasSelectedRows &&
    isCurrentFolderMatch &&
    Array.isArray(currentFolderDocuments) &&
    currentFolderDocuments.some((document) => hasCrossRefs(document));
  const canIncludeCrossRefs =
    isFolderTreeScope || hasCrossRefsInSelectedRows || hasCrossRefsInCurrentFolder;

  useEffect(() => {
    if (show) {
      reset();
      setShowAdvanced(false);
      setQueuedDownload(null);
    }
  }, [show, reset]);

  useEffect(() => {
    if (!canIncludeMerge) {
      setValue('includeMerge', false);
    } else if (hasMergeInSelectedRows || hasMergeInCurrentFolder) {
      setValue('includeMerge', true);
    }
  }, [canIncludeMerge, hasMergeInSelectedRows, hasMergeInCurrentFolder, setValue]);

  useEffect(() => {
    if (!burnCrossRefValue) {
      setValue('hyperlink', false);
    }
  }, [burnCrossRefValue, setValue]);

  useEffect(() => {
    if (!canIncludePublicHyperlinks) {
      setValue('publichyperlink', false);
    }
  }, [canIncludePublicHyperlinks, setValue]);

  useEffect(() => {
    if (!canIncludeCrossRefs) {
      setValue('burnCrossRef', false);
      setValue('hyperlink', false);
    }
  }, [canIncludeCrossRefs, setValue]);

  const isQueuedDownloadResponse = (response) => {
    if (!response || typeof response !== 'object') {
      return false;
    }

    return String(response.status || '').trim().toLowerCase() === 'queued';
  };

  const parseQueuedDownloadFromString = (response) => {
    if (typeof response !== 'string') {
      return null;
    }

    try {
      const parsedResponse = JSON.parse(response);
      return isQueuedDownloadResponse(parsedResponse) ? parsedResponse : null;
    } catch {
      return null;
    }
  };

  const parseQueuedDownloadFromBlob = async (response) => {
    if (!response?.blob || typeof response.blob.text !== 'function') {
      return null;
    }

    const blobType = response.blob.type || '';
    const isSmallResponse = response.blob.size <= QUEUED_RESPONSE_MAX_BYTES;
    const shouldParseBlob = blobType.includes('application/json') || isSmallResponse;

    if (!shouldParseBlob) {
      return null;
    }

    try {
      const bodyText = await response.blob.text();
      const parsedResponse = JSON.parse(bodyText);
      return isQueuedDownloadResponse(parsedResponse) ? parsedResponse : null;
    } catch {
      return null;
    }
  };

  const triggerDownloadFromUrl = (url, fileName, options = {}) => {
    const { openInNewTab = true } = options;
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener noreferrer';
    if (openInNewTab) {
      link.target = '_blank';
    }
    if (fileName) {
      link.download = fileName;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const tryAutoDownloadFromLink = async (link, fileName) => {
    try {
      const response = await fetch(link);
      if (!response.ok) {
        throw new Error(`Unexpected status code ${response.status}`);
      }
      const blob = await response.blob();
      saveAs(blob, fileName);
      return true;
    } catch (error) {
      console.error('Auto-download from notification link failed', error);
      return false;
    }
  };

  const handleManualQueuedDownload = () => {
    if (!queuedDownload?.link) return;
    triggerDownloadFromUrl(
      queuedDownload.link,
      getFileNameFromLink(queuedDownload.link, `${folder?.name || 'download'}.zip`),
    );
  };

  useEffect(() => {
    if (!queuedDownload?.jobId || queuedDownload?.link) {
      return;
    }

    fetchNotifications();
    const intervalId = window.setInterval(() => {
      fetchNotifications();
    }, 15000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [queuedDownload?.jobId, queuedDownload?.link, fetchNotifications]);

  useEffect(() => {
    if (!queuedDownload?.jobId) return;

    const allNotifications = [...(notifications || []), ...(transientNotifications || [])];
    if (!allNotifications.length) return;

    const matchingNotifications = allNotifications.filter((notification) =>
      doesNotificationMatchJobId(notification, queuedDownload.jobId),
    );

    const fallbackDownloadNotifications = allNotifications.filter((notification) => {
      const notificationLink = getNotificationDownloadLink(notification);
      if (!notificationLink) return false;
      if (queuedDownload?.queuedAt) {
        const notificationTimestamp = getNotificationTimestamp(notification);
        if (notificationTimestamp !== null && notificationTimestamp + 10000 < queuedDownload.queuedAt) {
          return false;
        }
      }
      return true;
    });

    const candidateNotifications = matchingNotifications.length
      ? matchingNotifications
      : fallbackDownloadNotifications;
    if (!candidateNotifications.length) return;

    const latestMatch = getLatestNotification(candidateNotifications);
    if (!latestMatch) return;

    const nextStatus = latestMatch?.status || queuedDownload.status;
    const nextMessage = latestMatch?.message || queuedDownload.message;
    const nextLink = getNotificationDownloadLink(latestMatch) || queuedDownload.link;
    const nextNotificationId = latestMatch?.id || queuedDownload.notificationId;

    setQueuedDownload((prev) => {
      if (!prev) return prev;
      if (
        prev.status === nextStatus &&
        prev.message === nextMessage &&
        prev.link === nextLink &&
        prev.notificationId === nextNotificationId
      ) {
        return prev;
      }

      return {
        ...prev,
        status: nextStatus,
        message: nextMessage,
        link: nextLink,
        notificationId: nextNotificationId,
      };
    });
  }, [queuedDownload?.jobId, queuedDownload?.link, notifications, transientNotifications]);

  useEffect(() => {
    if (!queuedDownload?.link || queuedDownload?.autoDownloadState !== 'idle') return;

    let isCancelled = false;

    const runAutoDownload = async () => {
      const fileName = getFileNameFromLink(queuedDownload.link, `${folder?.name || 'download'}.zip`);
      const started = await tryAutoDownloadFromLink(queuedDownload.link, fileName);

      if (isCancelled) return;

      setQueuedDownload((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          autoDownloadState: started ? 'started' : 'failed',
        };
      });
    };

    runAutoDownload();

    return () => {
      isCancelled = true;
    };
  }, [queuedDownload?.link, queuedDownload?.autoDownloadState, folder?.name]);

  const handleDownload = async (data) => {
    const selectedFileIds =
      selectedRows?.length > 0
        ? selectedRows.map((row) => row?.id || row?.fileId).filter(Boolean)
        : undefined;
    const scopeType =
      selectedFileIds && selectedFileIds.length > 0
        ? 'fileIds'
        : hasChildFolders(folder)
          ? 'folderTree'
          : 'folder';

    setDownloadInProgress(true);

    try {
      const { namingStrategy, prependBundleTab } = resolveDownloadNaming({
        filenameSelector: data?.filenameSelector,
        useTabNumbers: data?.useTabNumbers,
      });

      const response = await dispatch(
        actions.downloadFolder({
          folderId: folder?.id,
          scopeType,
          folderHasChildren: hasChildFolders(folder),
          ...(selectedFileIds && selectedFileIds.length > 0 && { fileIds: selectedFileIds }),
          ...data,
          namingStrategy,
          prependBundleTab,
          burnCrossRef: canIncludeCrossRefs ? !!data.burnCrossRef : false,
          hyperlink: canIncludeCrossRefs && !!data.burnCrossRef && !!data.hyperlink,
          includeMerge: canIncludeMerge ? !!data.includeMerge : false,
          publichyperlink: !!data.publichyperlink,
        }),
      );

      const queuedResponse = isQueuedDownloadResponse(response)
        ? response
        : parseQueuedDownloadFromString(response) || (await parseQueuedDownloadFromBlob(response));

      if (queuedResponse) {
        const jobId = queuedResponse?.jobId ? String(queuedResponse.jobId) : '';
        setQueuedDownload({
          jobId,
          status: queuedResponse?.status || 'queued',
          message: T.translate('case.downloadFolderModal.processingWaiting'),
          link: '',
          notificationId: null,
          autoDownloadState: 'idle',
          queuedAt: Date.now(),
        });
        if (jobId) {
          addPendingDownload({ jobId, label: folder?.name || '' });
        }
        return;
      }

      if (response?.blob) {
        saveAs(response.blob, response.fileName || `${folder?.name}.zip`);
      } else if (typeof response === 'string') {
        triggerDownloadFromUrl(response, `${folder?.name}.zip`);
      }

      handleClose();
    } catch (e) {
      console.error('Download failed', e);
    } finally {
      setDownloadInProgress(false);
    }
  };

  return (
    <Modal
      show={show}
      title={T.translate('case.downloadFolderModal.title')}
      handleClose={handleClose}
      handleSave={queuedDownload ? undefined : handleSubmit(handleDownload)}
      saveTitle={!queuedDownload
        ? downloadInProgress
          ? (
            <Spinner
              style={{
                height: '1.5rem',
              }}
            />
          )
          : (
            T.translate('generic.download')
          )
        : undefined}
      disableSave={downloadInProgress}
    >
      {queuedDownload ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: '1.05rem',
              color: '#111827',
            }}
          >
            {T.translate('case.downloadFolderModal.processingTitle')}
          </div>
          <div style={{ color: '#374151', lineHeight: 1.45 }}>
            {T.translate('case.downloadFolderModal.processingMessage')}
          </div>

          {!queuedDownload.link ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#4b5563',
              }}
            >
              <Spinner style={{ height: '1.25rem' }} />
              <span>{queuedDownload.message || T.translate('case.downloadFolderModal.processingWaiting')}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ color: '#374151' }}>
                {queuedDownload.autoDownloadState === 'started'
                  ? T.translate('case.downloadFolderModal.processingAutoDownloadStarted')
                  : T.translate('case.downloadFolderModal.processingAutoDownloadFallback')}
              </div>
              {queuedDownload.message &&
                queuedDownload.message !== T.translate('case.downloadFolderModal.processingWaiting') && (
                  <div style={{ color: '#4b5563', fontSize: '0.95rem' }}>{queuedDownload.message}</div>
                )}
              <Button
                type="button"
                variant="outlined"
                color="primary"
                style={{ width: 'fit-content' }}
                onClick={handleManualQueuedDownload}
              >
                {T.translate('case.downloadFolderModal.processingDownloadNow')}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Form.Group
            style={{
              marginBottom: '3rem',
            }}
          >
            <div
              style={{
                marginBottom: '1rem',
              }}
            >
              {hasSelectedRows
                ? T.translate('case.downloadFolderModal.downloadSelectedFilesMsg', {
                  count: selectedRows?.length || 0,
                })
                : T.translate('case.downloadFolderModal.downloadMsg', { folderName: folder?.name })}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Form.Label
                style={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#111827',
                  marginBottom: '0.35rem',
                }}
              >
                {T.translate('case.downloadFolderModal.versionLabel')}
              </Form.Label>
              <SelectFormAdapter
                control={control}
                name="source"
                label=""
                options={fileVersionOptions.map((value) => ({
                  label: T.translate(value.name),
                  value: value.version,
                }))}
                defaultValue={defaultFileVersionOption.version}
                placeholder={T.translate('generic.selectFileSource')}
                readOnly={downloadInProgress}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />

              <button
                type="button"
                onClick={() => setShowAdvanced((prev) => !prev)}
                style={{
                  alignSelf: 'flex-start',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  background: 'transparent',
                  border: 'none',
                  color: '#748CEC',
                  fontWeight: 700,
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <span>
                  {showAdvanced
                    ? T.translate('generic.hideAdvancedSettings')
                    : T.translate('generic.showAdvancedSettings')}
                </span>
                <FontAwesomeIcon icon={showAdvanced ? faChevronUp : faChevronDown} size="sm" />
              </button>

              {showAdvanced && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Form.Label
                      style={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#111827',
                        marginBottom: '0.25rem',
                      }}
                    >
                      {T.translate('case.downloadFolderModal.optionsLabel')}
                    </Form.Label>
                    <Controller
                      control={control}
                      name="burnId"
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={(event, checked) => field.onChange(checked)}
                              disabled={downloadInProgress}
                            />
                          }
                          label={T.translate('generic.downloadIncludeFileDetails')}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="burnExternalId"
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={(event, checked) => field.onChange(checked)}
                              disabled={downloadInProgress}
                            />
                          }
                          label={T.translate('generic.downloadIncludeExternalId')}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="burnCrossRef"
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={(event, checked) => {
                                field.onChange(checked);
                                if (!checked) {
                                  setValue('hyperlink', false);
                                }
                              }}
                              disabled={downloadInProgress || !canIncludeCrossRefs}
                            />
                          }
                          label={T.translate('case.includeCrossRef')}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="hyperlink"
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={(event, checked) => field.onChange(checked)}
                              disabled={downloadInProgress || !canIncludeCrossRefs || !showHyperlinkToggle}
                            />
                          }
                          label={T.translate('case.convertCrossRefToExternalHyperlink')}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="publichyperlink"
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={(event, checked) => field.onChange(checked)}
                              disabled={downloadInProgress || !canIncludePublicHyperlinks}
                            />
                          }
                          label={T.translate('case.includePublicHyperLinks')}
                        />
                      )}
                    />
                    {canIncludeMerge && (
                      <Controller
                        control={control}
                        name="includeMerge"
                        render={({ field }) => (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                checked={!!field.value}
                                onChange={(event, checked) => {
                                  field.onChange(checked);
                                  setValue('includeMerge', checked);
                                }}
                                disabled={downloadInProgress}
                              />
                            }
                            label={T.translate('case.downloadFolderModal.includeMergeFiles')}
                          />
                        )}
                      />
                    )}
                    <Controller
                      control={control}
                      name="useTabNumbers"
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={(event, checked) => {
                                field.onChange(checked);
                                setValue('useTabNumbers', checked);
                              }}
                              disabled={downloadInProgress}
                            />
                          }
                          label={T.translate('case.downloadFolderModal.prependFileWithTabLabel')}
                        />
                      )}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <Form.Label
                      style={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#111827',
                        marginBottom: '0.25rem',
                      }}
                    >
                      {T.translate('case.downloadFolderModal.downloadFileNameLabel')}
                    </Form.Label>
                    <SelectFormAdapter
                      control={control}
                      name="filenameSelector"
                      label=""
                      options={fileNameOptions.map((value) => ({
                        label: T.translate(value.name),
                        value: value.fileName,
                      }))}
                      defaultValue={fileNameOptions[0].fileName}
                      readOnly={downloadInProgress}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </>
              )}
            </div>
          </Form.Group>
        </Form>
      )}
    </Modal>
  );
};
DownloadModal.propTypes = {
  isBundleTabPage: PropTypes.bool,
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  selectedRows: PropTypes.array,
  folder: PropTypes.object,
};

export default DownloadModal;
